import { Link, useLoaderData } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { LinkButton } from "../../components/common";
import { MEMBERSHIP_TYPES } from "../../schemas";
import type { NetworkListData } from "../../routes/loaders";
import type { Role } from "../../types/user";
import styles from "./NetworkListPage.module.css";

export default function NetworkListPage() {
  const { networks, organizationId } = useLoaderData() as NetworkListData;
  const { user } = useAuth();

  const userRole: Role | undefined = user?.organizations.find(
    (org) => org.id === organizationId,
  )?.role;

  const canCreateNetwork = userRole === "owner" || userRole === "admin";

  return (
    <div className="container">
      <div className={styles.pageHeader}>
        <div>
          <h1>Networks</h1>
          <Link to={`/organizations/${organizationId}`} className={styles.backLink}>
            Back to Organization
          </Link>
        </div>
        {canCreateNetwork && (
          <LinkButton to={`/organizations/${organizationId}/networks/create`} variant="primary">
            Create Network
          </LinkButton>
        )}
      </div>

      {networks.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
          </div>
          <h2>No networks yet</h2>
          <p>Create a network to group and manage related podcasts together.</p>
          {canCreateNetwork && (
            <LinkButton to={`/organizations/${organizationId}/networks/create`} variant="primary">
              Create Your First Network
            </LinkButton>
          )}
        </div>
      ) : (
        <div className={styles.networkGrid}>
          {networks.map((network) => (
            <Link
              key={network.id}
              to={`/organizations/${organizationId}/networks/${network.id}/settings`}
              className={styles.networkCard}
            >
              <div className={styles.networkCardHeader}>
                {network.logoUrl ? (
                  <img src={network.logoUrl} alt={network.name} className={styles.networkLogo} />
                ) : (
                  <div className={styles.networkLogoPlaceholder}>
                    {network.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <h3>{network.name}</h3>
              </div>
              {network.description && (
                <p className={styles.networkDescription}>{network.description}</p>
              )}
              <div className={styles.networkMeta}>
                <span className={styles.membershipBadge}>
                  {MEMBERSHIP_TYPES.find((t) => t.value === network.membershipType)?.label ||
                    network.membershipType}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
